import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ExternalLink, Eye } from 'lucide-react'
import { portfolioData, portfolioCategories, ProjectData } from '../lib/data.ts'
import { staggerContainer, staggerItem, modalBackdrop, modalContent } from '../lib/motion.ts'
import SectionTitle from './SectionTitle.tsx'

const Portfolio = () => {
  const [activeCategory, setActiveCategory] = useState('All')
  const [selectedProject, setSelectedProject] = useState<ProjectData | null>(null)

  const filteredProjects = portfolioData.filter(project => 
    activeCategory === 'All' || project.category === activeCategory
  )

  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'web':
        return 'bg-green-500'
      case 'app':
        return 'bg-accent'
      case 'brand':
        return 'bg-purple-500'
      default:
        return 'bg-accent'
    }
  }

  const openModal = (project: ProjectData) => {
    setSelectedProject(project)
    document.body.style.overflow = 'hidden'
  }

  const closeModal = () => {
    setSelectedProject(null)
    document.body.style.overflow = 'unset'
  }

  return (
    <section id="portfolio" className="section-padding bg-gray-50">
      <div className="container">
        <SectionTitle
          subtitle="Portfolio"
          title="Solutions That Just Work."
          description="Our work delivers reliable, user-friendly solutions that solve real problems. Explore the projects where design meets function seamlessly."
        />

        {/* Filter Tabs */}
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="flex flex-wrap justify-center gap-2 mt-12 mb-16"
        >
          {portfolioCategories.map((category) => (
            <motion.button
              key={category}
              variants={staggerItem}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeCategory === category
                  ? 'bg-primary text-white shadow-lg'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category}
            </motion.button>
          ))}
        </motion.div>

        {/* Portfolio Grid */}
        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <AnimatePresence>
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group cursor-pointer"
                onClick={() => openModal(project)}
              >
                <div className="relative overflow-hidden rounded-xl bg-gray-100 aspect-[4/3]">
                  {/* Image */}
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-white font-display font-bold text-lg mb-1">
                            {project.title}
                          </h3>
                          <p className="text-gray-300 text-sm">
                            {project.client} • {project.year}
                          </p>
                        </div>
                        <motion.div
                          className="bg-white/20 backdrop-blur-sm rounded-full p-3"
                          whileHover={{ scale: 1.1 }}
                        >
                          <Eye size={20} className="text-white" />
                        </motion.div>
                      </div>
                    </div>
                  </div>

                  {/* Category Badge */}
                  <div className="absolute top-4 left-4">
                    {project.inProgress ? (
                      <span className="inline-block bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                        In Progress
                      </span>
                    ) : (
                      <span className={`inline-block ${getCategoryColor(project.category)} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                        {project.category}
                      </span>
                    )}
                  </div>

                  {/* Featured Badge */}
                  {project.featured && (
                    <div className="absolute top-4 right-4">
                      <span className="inline-block bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                        Featured
                      </span>
                    </div>
                  )}
                </div>

                {/* Project Info */}
                <div className="mt-4">
                  <h3 className="font-display font-bold text-primary mb-2">
                    {project.title}
                  </h3>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {project.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

      </div>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            variants={modalBackdrop}
            initial="initial"
            animate="animate"
            exit="exit"
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center md:p-4"
            onClick={closeModal}
          >
            <motion.div
              variants={modalContent}
              initial="initial"
              animate="animate"
              exit="exit"
              className="bg-white md:rounded-2xl md:max-w-4xl w-full md:max-h-[90vh] h-full md:h-auto overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 md:top-6 md:right-6 z-10 bg-black/20 backdrop-blur-sm rounded-full p-2 text-white hover:bg-black/40 transition-colors"
              >
                <X size={24} />
              </button>

              {/* Modal Content */}
              <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
                {/* Media */}
                <div className="relative bg-gray-100 h-80 md:h-96 lg:h-auto">
                  {selectedProject.video ? (
                    <video
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="w-full h-full object-cover object-top"
                    >
                      <source src={selectedProject.video} type="video/mp4" />
                    </video>
                  ) : (
                    <img
                      src={selectedProject.image}
                      alt={selectedProject.title}
                      className="w-full h-full object-cover object-top"
                    />
                  )}
                  
                  {/* Project Type Badge - Overlay */}
                  <div className="absolute top-4 left-4 z-10">
                    <span className={`inline-block text-white text-sm font-bold px-3 py-1 rounded-full ${getCategoryColor(selectedProject.category)}`}>
                      {selectedProject.category}
                    </span>
                  </div>
                </div>

                {/* Details */}
                <div className="p-6 md:p-8 lg:p-12 overflow-y-auto flex-1">
                  <div className="mb-4">
                    <h2 className="text-3xl font-display font-bold text-gray-900 mb-2">
                      {selectedProject.title}
                    </h2>
                    <p className="text-gray-600">
                      {selectedProject.client} • {selectedProject.year}
                    </p>
                  </div>

                  <p className="text-gray-700 leading-relaxed mb-6">
                    {selectedProject.description}
                  </p>

                  <div className="mb-6">
                    <h4 className="font-display font-bold text-gray-900 mb-3">
                      Services
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-sm text-gray-700 bg-gray-100 px-3 py-1 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="pt-6 border-t border-gray-200">
                    {selectedProject.inProgress ? (
                      <button
                        className="w-full flex items-center justify-center bg-gray-300 text-gray-500 px-6 py-3 rounded-lg font-medium cursor-not-allowed"
                        disabled
                      >
                        IN PROGRESS (Coming Soon)
                      </button>
                    ) : selectedProject.liveUrl ? (
                      <a
                        href={selectedProject.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary w-full flex items-center justify-center"
                      >
                        View Project
                        <ExternalLink size={16} className="ml-2" />
                      </a>
                    ) : (
                      <button
                        className="btn-primary w-full flex items-center justify-center opacity-50 cursor-not-allowed"
                        disabled
                      >
                        View Project
                        <ExternalLink size={16} className="ml-2" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}

export default Portfolio
